import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin",
  consultant: "Consultant",
  client_viewer: "Client · Viewer",
};

const ROLE_COLOR = {
  admin: "text-[color:var(--accent)] border-[color:var(--accent)]/50",
  consultant: "text-[color:var(--info)] border-[color:var(--info)]/50",
  client_viewer: "text-[color:var(--text-2)] border-[color:var(--border-strong)]",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "consultant",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [u, w] = await Promise.all([
      api.get("/api/users"),
      api.get("/api/workspace"),
    ]);
    setUsers(u.data);
    setWorkspace(w.data);
  };

  useEffect(() => {
    load();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/users/invite", form);
      toast.success("User provisioned");
      setForm({ name: "", email: "", password: "", role: "consultant" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from workspace?`)) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      toast.success("User removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="p-6 md:p-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">/ workspace · {workspace?.name || "…"}</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
            Users & access.
          </h1>
          <p className="mt-2 text-[color:var(--text-2)] max-w-2xl">
            Multi-tenant workspace. Invite consultants and read-only client
            viewers — all data stays scoped to this tenant.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="show-invite-btn"
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-5 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
          >
            <UserPlus size={14} /> Invite User
          </button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={invite}
          data-testid="invite-form"
          className="border border-[color:var(--accent)]/40 bg-[color:var(--bg-1)] p-6 space-y-4"
        >
          <div className="eyebrow text-[color:var(--accent)]">/ new user</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Full name"
              tid="invite-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <Field
              label="Email"
              tid="invite-email"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              required
            />
            <Field
              label="Temporary password"
              tid="invite-password"
              type="text"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              required
            />
            <label className="block">
              <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-2)]">
                Role
              </span>
              <select
                data-testid="invite-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1.5 w-full h-11 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 mono text-sm text-white focus:border-[color:var(--accent)] focus:outline-none"
              >
                <option value="consultant">Consultant</option>
                <option value="client_viewer">Client · Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="invite-submit"
              type="submit"
              disabled={busy}
              className="bg-[color:var(--accent)] text-black h-10 px-4 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest disabled:opacity-50"
            >
              {busy ? "provisioning…" : "provision"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="border border-[color:var(--border-strong)] h-10 px-4 hover:border-white transition-colors duration-150 mono text-xs uppercase tracking-widest"
            >
              cancel
            </button>
          </div>
        </form>
      )}

      <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)]">
        <table className="w-full mono text-sm">
          <thead>
            <tr className="border-b border-[color:var(--border)] text-[color:var(--text-3)]">
              <th className="text-left p-4 uppercase tracking-widest text-[10px] font-normal">
                User
              </th>
              <th className="text-left p-4 uppercase tracking-widest text-[10px] font-normal">
                Email
              </th>
              <th className="text-left p-4 uppercase tracking-widest text-[10px] font-normal">
                Role
              </th>
              <th className="text-left p-4 uppercase tracking-widest text-[10px] font-normal">
                Joined
              </th>
              {isAdmin && (
                <th className="text-right p-4 uppercase tracking-widest text-[10px] font-normal">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="border-b border-[color:var(--border)] hover:bg-[color:var(--bg-2)] transition-colors duration-150"
              >
                <td className="p-4">
                  <div className="text-white text-sm">{u.name}</div>
                </td>
                <td className="p-4 text-[color:var(--text-2)]">{u.email}</td>
                <td className="p-4">
                  <span
                    className={`mono text-[10px] uppercase tracking-widest border px-2 py-1 ${
                      ROLE_COLOR[u.role]
                    }`}
                  >
                    {ROLE_LABEL[u.role]}
                  </span>
                </td>
                <td className="p-4 text-[color:var(--text-3)] text-xs">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                {isAdmin && (
                  <td className="p-4 text-right">
                    {u.id !== user.id && (
                      <button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => remove(u)}
                        className="text-[color:var(--text-3)] hover:text-[color:var(--critical)] transition-colors duration-150"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, tid, value, onChange, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-2)]">
        {label}
      </span>
      <input
        data-testid={tid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-11 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 mono text-sm text-white placeholder:text-[color:var(--text-3)] focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] transition-colors duration-150"
        {...rest}
      />
    </label>
  );
}
